/**
 * Configuration for the TRUS-686 workflow steps, rendered by
 * StepConfigurationPanel:
 *
 *  - Post to channel (`notify_channel`): channel picker (private only for a
 *    member workflow), message template, "include a link".
 *  - Notify staff (`notify_staff`): recipients (assignee, case manager, roles,
 *    people), bell / push, message template.
 *
 * Both complete immediately when the workflow runs (they never wait). Issues
 * (missing channel / recipients / message, variables outside the allow-list)
 * are listed under the form; the builder pages block Add / Save on them.
 */

import React, { useEffect } from 'react';
import { Bell, Smartphone } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { ChannelPicker } from './ChannelPicker';
import { MessageTemplateEditor } from './MessageTemplateEditor';
import { StaffRecipientsEditor } from './StaffRecipientsEditor';
import {
  NOTIFY_CHANNEL_STEP,
  defaultNotifyConfig,
  notifyIssueMessage,
  notifyStepIssues,
  type NotifyStepType,
  type StaffRecipients,
} from './notifySteps';

const CHANNEL_PLACEHOLDER = '{{workflow_name}} · {{step_name}} · {{due_date}} · {{member_first_initial}}';
const STAFF_PLACEHOLDER = '{{step_name}} · {{program_name}} · {{link}}';

interface NotifyStepConfigProps {
  stepType: NotifyStepType;
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
  /** The workflow is about a member: channels must be private. */
  memberBound: boolean;
}

export function NotifyStepConfig({ stepType, config, onChange, memberBound }: NotifyStepConfigProps) {
  const { t } = useAppTranslation();
  // The builder pages start a new step with {}: seed this step's defaults once.
  const isEmpty = !config || Object.keys(config).length === 0;
  useEffect(() => {
    if (isEmpty) onChange(defaultNotifyConfig(stepType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty, stepType]);
  const set = (patch: Record<string, any>) => onChange({ ...config, ...patch });
  // Unknown variables are flagged by the editor itself; list the rest here.
  const issues = notifyStepIssues(stepType, config).filter((i) => i.code !== 'unknown_variables');
  const channels: string[] = Array.isArray(config.channels) ? config.channels : [];

  return (
    <div className="space-y-4" data-testid={`notify-step-config-${stepType}`}>
      <p className="text-xs text-muted-foreground">
        {stepType === NOTIFY_CHANNEL_STEP
          ? t('workflows.notify.channelStepHint', 'WeaveIn posts this message to the channel when the workflow reaches this step, then moves on.')
          : t('workflows.notify.staffStepHint', 'These staff get a notification when the workflow reaches this step, then it moves on.')}
      </p>

      {stepType === NOTIFY_CHANNEL_STEP ? (
        <>
          <ChannelPicker
            id="notify-step-channel"
            value={Number(config.channel_id) > 0 ? Number(config.channel_id) : null}
            onChange={(channelId) => set({ channel_id: channelId })}
            privateOnly={memberBound}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--primary,#2563EB)]"
              checked={config.include_link !== false}
              onChange={(e) => set({ include_link: e.target.checked })}
              data-testid="notify-step-include-link"
            />
            {t('workflows.notify.includeLink', 'Include a link to the workflow')}
          </label>
        </>
      ) : (
        <>
          <StaffRecipientsEditor
            idPrefix="notify-step"
            value={(config.recipients as StaffRecipients) || {}}
            onChange={(recipients) => set({ recipients })}
            allowCaseManager={memberBound}
            assigneeLabel={t('workflows.notify.stepAssignee', 'Whoever this workflow is assigned to')}
          />
          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-sm font-medium">{t('workflows.notify.deliverBy', 'Notify by')}</legend>
            {(
              [
                ['bell', Bell, t('workflows.notify.bell', 'Bell')],
                ['push', Smartphone, t('workflows.notify.push', 'Push')],
              ] as const
            ).map(([value, Icon, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--primary,#2563EB)]"
                  checked={channels.includes(value)}
                  onChange={(e) =>
                    set({
                      channels: e.target.checked
                        ? [...channels.filter((c) => c !== value), value]
                        : channels.filter((c) => c !== value),
                    })
                  }
                  data-testid={`notify-step-deliver-${value}`}
                />
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                {label}
              </label>
            ))}
          </fieldset>
        </>
      )}

      <MessageTemplateEditor
        id="notify-step-message"
        label={t('workflows.notify.message', 'Message')}
        value={String(config.message_template || '')}
        onChange={(message_template) => set({ message_template })}
        // Variables only: they are not translated, and t() would interpolate them away.
        placeholder={stepType === NOTIFY_CHANNEL_STEP ? CHANNEL_PLACEHOLDER : STAFF_PLACEHOLDER}
      />

      {issues.length > 0 && (
        <ul className="space-y-0.5 text-xs text-destructive" data-testid="notify-step-issues">
          {issues.map((issue) => (
            <li key={issue.code}>{notifyIssueMessage(issue, t)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default NotifyStepConfig;
